import React from 'react';
import {
  Heart,
  ExternalLink,
  Trash2,
  Pencil,
  Check,
  Loader2,
  Mail,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackExternalLinkClick } from '@/utils/analytics';

/**
 * Footer Actions Component
 * 
 * Provides context-appropriate action buttons based on modal mode
 * Sticky footer with different actions for discovery, user_program, and edit modes
 * 
 * @param {Object} props
 * @param {string} props.mode - 'discovery' | 'user_program'
 * @param {boolean} props.isEditMode - Whether in edit mode
 * @param {Object} props.camp - Camp data with website URL
 * @param {boolean} [props.isSaving] - Loading state for save operation
 * @param {Function} [props.onSaveForLater] - Handler for save for later action
 * @param {Function} [props.onDelete] - Handler for delete action
 * @param {Function} [props.onToggleEditMode] - Handler for toggling edit mode
 * @param {Function} [props.onCancelEdit] - Handler for canceling edit
 * @param {Function} [props.onSaveChanges] - Handler for saving changes
 * @param {Function} [props.onContactProgram] - Handler for opening contact program modal
 */
const FooterActions = ({
  mode,
  isEditMode,
  camp,
  isSaving = false,
  onSaveForLater = null,
  onDelete = null,
  onToggleEditMode = null,
  onCancelEdit = null,
  onSaveChanges = null,
  onContactProgram = null,
}) => {
  // Helper function to open website in webview or new tab
  const openWebsiteInWebview = (url) => {
    if (!url) return;

    // Track external link click
    trackExternalLinkClick({
      url: url,
      source: mode === 'discovery' ? 'discovery_modal' : 'program_modal',
      camp_id: camp?.id,
    });

    // Ensure URL has protocol
    let fullUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      fullUrl = `https://${url}`;
    }

    // Mobile: Try to open in in-app browser if available
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'OPEN_WEBVIEW',
          url: fullUrl,
        })
      );
    }
    // Desktop: Open in new tab
    else {
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Get website URL from various possible fields
  const websiteUrl = camp?.website_url || camp?.website || null;

  return (
    <div className="sticky bottom-0 bg-white border-t border-gray-200 shadow-lg z-10">
      <div className="max-w-4xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-4 flex-col sm:flex-row">
          {/* Discovery Mode */}
          {mode === 'discovery' && (
            <>
              {/* Save for Later */}
              <Button
                variant="outline"
                size="lg"
                onClick={onSaveForLater}
                className="flex-1 w-full sm:w-auto"
              >
                <Heart className="w-5 h-5 mr-2" />
                Save for Later
              </Button>

              {/* Contact Program */}
              {onContactProgram && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onContactProgram}
                  className="flex-1 w-full sm:w-auto border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Contact Program
                </Button>
              )}

              {/* Visit Website */}
              <Button
                variant="default"
                size="lg"
                onClick={() => openWebsiteInWebview(websiteUrl)}
                className="flex-1 w-full sm:w-auto bg-gradient-to-r from-brand-600 to-blue-600 hover:from-brand-700 hover:to-blue-700 text-white"
                disabled={!websiteUrl}
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Visit Website to Register
              </Button>
            </>
          )}

          {/* User Program Mode (not editing) */}
          {mode === 'user_program' && !isEditMode && (
            <>
              {/* Delete */}
              <Button
                variant="ghost"
                size="lg"
                onClick={onDelete}
                className="text-red-600 hover:bg-red-50 w-full sm:w-auto"
              >
                <Trash2 className="w-5 h-5 mr-2" />
                Delete
              </Button>

              <div className="flex gap-3 flex-1 w-full sm:w-auto">
                {/* Visit Website */}
                {websiteUrl && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => openWebsiteInWebview(websiteUrl)}
                    className="flex-1"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Visit Website
                  </Button>
                )}

                {/* Edit */}
                <Button
                  variant="default"
                  size="lg"
                  onClick={onToggleEditMode}
                  className="flex-1 bg-gradient-to-r from-brand-600 to-blue-600 hover:from-brand-700 hover:to-blue-700 text-white"
                >
                  <Pencil className="w-5 h-5 mr-2" />
                  Edit Program
                </Button>
              </div>
            </>
          )}

          {/* Edit Mode */}
          {isEditMode && (
            <>
              {/* Cancel */}
              <Button
                variant="outline"
                size="lg"
                onClick={onCancelEdit}
                className="flex-1 w-full sm:w-auto"
                disabled={isSaving}
              >
                Cancel
              </Button>

              {/* Save Changes */}
              <Button
                variant="default"
                size="lg"
                onClick={() => {
                  // Trigger form submission
                  const form = document.getElementById('edit-program-form');
                  if (form) {
                    form.requestSubmit();
                  }
                }}
                className="flex-1 w-full sm:w-auto bg-gradient-to-r from-brand-600 to-blue-600 hover:from-brand-700 hover:to-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSaving}
                type="button"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FooterActions;

